import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import IconStatic from './IconStatic';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import GoogleMap from './GoogleMap';
import InputLocationWithPlaces from './InputLocationWithPlaces';
import ConsentOverlay from '../../components/ConsentOverlay/ConsentOverlay';
import { ZOOM_DEFAULTS } from './LocationFinderTypes';
import type { I18nStrings, Location, LocationCardProps, LocationFinderProps } from './LocationFinderTypes';
import { InputSizeTypes } from '../../components/Form/Form.types';
import { cmpLoaded, getVendorConsent, setVendorConsent } from '../../assets/TypeScript/ConsentManager';

export const LocationFinder: React.FC<LocationFinderProps> = ({
    apiEndpoint,
    instanceId,
    searchRadiusOptions,
    listAndFilterBelow: _listAndFilterBelow = false,
    preselectedCompany = '',
    preselectedArea = '',
    defaultRadius = '10',
    enableAreaFilter = false,
    defaultCenterLat = 51.5,
    defaultCenterLng = 10.5,
    limitNearestLocations: _limitNearestLocations = 10,
    className,
    topline,
    headline,
    description,
    i18n = {},
    googleMapsApiKey,
    useZoomlevel = false,
    usePrioritySort = false,
    zoomSingleMarker = ZOOM_DEFAULTS.SINGLE_MARKER,
    zoomSelectedLocation = ZOOM_DEFAULTS.SELECTED_LOCATION,
    zoomInitial = ZOOM_DEFAULTS.INITIAL,
}) => {
    // Every id and every element lookup is scoped to this value, so a page may carry more than one
    // instance without them fighting over the same DOM ids. TYPO3 passes the content element uid.
    const fallbackInstanceId = useId();
    const instanceScope = instanceId || fallbackInstanceId;
    // Lookups run through this ref instead of document, so an instance only ever finds its own nodes.
    const rootRef = useRef<HTMLDivElement>(null);

    // The selectable radii come from plugin.tx_tnlocationfinder.settings.searchRadiusOptions via the
    // template, so the FlexForm dropdown and this select cannot drift apart. The literal below is only
    // the Storybook fallback, where no TypoScript exists.
    const radiusOptions = useMemo(() => (searchRadiusOptions?.length ? searchRadiusOptions : [10, 25, 50, 100, 150]), [searchRadiusOptions]);
    const radiusLabels: Record<number, string | undefined> = useMemo(
        () => ({ 10: i18n.radiusOption10, 25: i18n.radiusOption25, 50: i18n.radiusOption50, 100: i18n.radiusOption100, 150: i18n.radiusOption150 }),
        [i18n]
    );

    // Google Maps API Key - Must be provided via props
    const GOOGLE_MAPS_API_KEY = googleMapsApiKey;

    // Warn if no API key provided
    if (!GOOGLE_MAPS_API_KEY) {
        console.error('[LocationFinder] No Google Maps API key provided. Please set data-google-maps-api-key attribute.');
    }

    // State
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [googleLoadError, setGoogleLoadError] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
    const [scrollToLocation, setScrollToLocation] = useState<boolean>(false);
    const [mapCenter, setMapCenter] = useState({ lat: defaultCenterLat, lng: defaultCenterLng });
    const [searchPosition, setSearchPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [shouldAutoSelectClosestLocation, setShouldAutoSelectClosestLocation] = useState(false);
    const [forceZoomToLocation, setForceZoomToLocation] = useState(false);
    const [hasConsent, setHasConsent] = useState(false);

    // Track viewport to avoid rendering both map instances simultaneously
    const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));

    useEffect(() => {
        const mql = window.matchMedia('(min-width: 1024px)');
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        cmpLoaded(() => {
            setHasConsent(getVendorConsent('s135'));
        });
    }, []);

    const handleConsentAccept = useCallback(() => {
        setVendorConsent('s135');
        setHasConsent(true);
    }, []);

    // Cache for Geocoder instance - reuse instead of creating new instance per search (efficiency fix)
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);

    // Helper function to get URL parameters
    const getUrlParam = (name: string): string | null => {
        if (typeof window === 'undefined') return null;
        const params = new URLSearchParams(window.location.search);
        return params.get(name);
    };

    // Helper function to update URL parameters
    const updateUrlParam = (name: string, value: string) => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set(name, value);
        } else {
            url.searchParams.delete(name);
        }
        window.history.replaceState({}, '', url.toString());
    };

    // Track search input value via ref (no re-render on keystroke)
    const searchTermRef = useRef('');
    const [inputResetKey, setInputResetKey] = useState(0);

    // Active filters (applied to results)
    const [selectedCompany, setSelectedCompany] = useState(getUrlParam('company') || preselectedCompany);
    const [selectedArea, setSelectedArea] = useState(enableAreaFilter ? getUrlParam('area') || preselectedArea : '');
    const [selectedRadius, setSelectedRadius] = useState(getUrlParam('radius') || defaultRadius);

    // Pending filters (shown in UI but not yet applied)
    const [pendingCompany, setPendingCompany] = useState(getUrlParam('company') || preselectedCompany);
    const [pendingArea, setPendingArea] = useState(enableAreaFilter ? getUrlParam('area') || preselectedArea : '');
    const [pendingRadius, setPendingRadius] = useState(getUrlParam('radius') || defaultRadius);

    // Update URL when filters change
    useEffect(() => {
        updateUrlParam('company', selectedCompany);
    }, [selectedCompany]);

    useEffect(() => {
        if (enableAreaFilter) {
            updateUrlParam('area', selectedArea);
        }
    }, [selectedArea, enableAreaFilter]);

    // Keep the radius in the URL so it is shareable/restorable like company & area.
    // The configured default is kept out of the URL to avoid noise; only deviations are stored.
    useEffect(() => {
        updateUrlParam('radius', selectedRadius === defaultRadius ? '' : selectedRadius);
    }, [selectedRadius, defaultRadius]);

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const company = getUrlParam('company') || '';
            setSelectedCompany(company);

            if (enableAreaFilter) {
                const area = getUrlParam('area') || '';
                setSelectedArea(area);
            }

            const radius = getUrlParam('radius') || defaultRadius;
            setSelectedRadius(radius);
            setPendingRadius(radius);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [enableAreaFilter, defaultRadius]);

    // Distance calculation helper function
    const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): number => {
        const R = 6371; // Earth's radius in kilometers
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLng = ((lng2 - lng1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in kilometers
    }, []);

    // i18n — memoized to avoid re-creating on every render
    const t: I18nStrings = useMemo(
        () => ({
            topline: topline || i18n.topline || undefined,
            headline: headline || i18n.headline || undefined,
            description: description || i18n.description || undefined,
            searchPlaceholder: i18n.searchPlaceholder || 'z.B. 30161 oder Hannover',
            searchLabel: i18n.searchLabel || 'Postleitzahl oder Ort',
            filterShow: i18n.filterShow || 'Filter einblenden',
            filterHide: i18n.filterHide || 'Filter ausblenden',
            companyLabel: i18n.companyLabel || 'Gesellschaft',
            areaLabel: i18n.areaLabel || 'Bereich',
            radiusLabel: i18n.radiusLabel || 'Umkreis',
            noResults: i18n.noResults || 'Keine Standorte gefunden. Vergrößern Sie den Umkreis, um weitere Standorte zu finden.',
            loading: i18n.loading || 'Standorte werden geladen...',
            filterReset: i18n.filterReset || 'Filter zurücksetzen',
            searchButton: i18n.searchButton || 'Standort suchen',
            showOnMap: i18n.showOnMap || 'Auf der Karte anzeigen',
            details: i18n.details || 'Details anzeigen',
            useMyLocation: i18n.useMyLocation || 'Meinen Standort verwenden',
            locationNotSupported: i18n.locationNotSupported || 'Ihr Browser unterstützt keine Standortbestimmung.',
            locationError: i18n.locationError || 'Standortbestimmung fehlgeschlagen. Bitte geben Sie Ihren Standort manuell ein.',
            locationDenied: i18n.locationDenied || 'Der Zugriff auf Ihren Standort wurde abgelehnt.',
            locationUnavailable: i18n.locationUnavailable || 'Ihr Standort ist derzeit nicht verfügbar.',
            locationTimeout: i18n.locationTimeout || 'Die Standortbestimmung hat zu lange gedauert.',
            resultsFound: i18n.resultsFound || 'Standorte gefunden',
            selectPlaceholder: i18n.selectPlaceholder || 'Bitte auswählen',
            selectAll: i18n.selectAll || 'Alle',
            radiusOption10: i18n.radiusOption10 || '10km',
            radiusOption25: i18n.radiusOption25 || '25km',
            radiusOption50: i18n.radiusOption50 || '50km',
            radiusOption100: i18n.radiusOption100 || '100km',
            radiusOption150: i18n.radiusOption150 || '150km',
            listView: i18n.listView || 'Liste',
            mapView: i18n.mapView || 'Karte',
            phoneLabel: i18n.phoneLabel || 'Tel.',
            googleMapsError: i18n.googleMapsError || 'Google Maps konnte nicht geladen werden.',
            overlayText: i18n.overlayText || 'Um diesen Inhalt anzuzeigen, müssen Sie der Verwendung von Cookies zustimmen.',
            overlayLabel: i18n.overlayLabel || 'Cookies akzeptieren',
        }),
        [i18n, topline, headline, description]
    );

    // Load Google Maps API (once) using modern @googlemaps/js-api-loader
    useEffect(() => {
        // Don't load without an API key or before consent is given
        if (!GOOGLE_MAPS_API_KEY || !hasConsent) {
            return;
        }

        if (window.google) {
            // Avoid synchronous setState in effect
            Promise.resolve().then(() => setGoogleLoaded(true));
            return;
        }

        // Use dynamic import to avoid bundle size impact
        const loadGoogleMaps = async () => {
            try {
                // Import Loader dynamically
                const { Loader } = await import('@googlemaps/js-api-loader');

                const loader = new Loader({
                    apiKey: GOOGLE_MAPS_API_KEY,
                    version: 'weekly',
                    libraries: ['places', 'marker'],
                });

                // Load the maps library to initialize google.maps
                await loader.load();
                setGoogleLoaded(true);
            } catch (error) {
                console.error('[LocationFinder] Failed to load Google Maps API:', error);
                setGoogleLoadError(true);
            }
        };

        loadGoogleMaps();
    }, [GOOGLE_MAPS_API_KEY, hasConsent]);

    // Initialize Geocoder once when Google Maps loads (efficiency: reuse single instance)
    useEffect(() => {
        if (googleLoaded && window.google?.maps?.Geocoder) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
    }, [googleLoaded]);

    // Load locations (once)
    useEffect(() => {
        fetch(apiEndpoint)
            .then((res) => res.json())
            .then((data) => {
                const locationData = Array.isArray(data) ? data : data.locations || data.Locations || [];
                setLocations(
                    locationData.map((loc: Location) => ({
                        ...loc,
                        visibility: loc.visibility !== undefined ? loc.visibility : true,
                    }))
                );
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching locations:', error);
                setLoading(false);
            });
    }, [apiEndpoint]);

    // Filter locations with distance calculation
    const filteredLocations = useMemo(() => {
        let filtered = locations.filter((loc) => loc.visibility !== false);

        // Apply company filter
        if (selectedCompany) {
            filtered = filtered.filter((location) => location.company === selectedCompany);
        }

        // Apply area filter (only if enabled)
        if (enableAreaFilter && selectedArea) {
            filtered = filtered.filter((location) => location.area === selectedArea);
        }

        // Calculate distances and apply radius filter if search position is set
        if (searchPosition) {
            const radius = parseInt(selectedRadius, 10);

            filtered = filtered
                .map((location) => {
                    const distance = calculateDistance(searchPosition.lat, searchPosition.lng, location.latitude, location.longitude);
                    return {
                        ...location,
                        distance: distance * 1000, // Convert to meters for consistency with the interface
                    };
                })
                .filter((location) => {
                    // Only show locations within the selected radius
                    return (location.distance || 0) <= radius * 1000; // Convert km to meters
                })
                .sort((a, b) => {
                    // Sort by distance (closest first), priority as tiebreaker
                    const distDiff = (a.distance || 0) - (b.distance || 0);
                    if (usePrioritySort && distDiff === 0) {
                        return (b.priority || 0) - (a.priority || 0);
                    }
                    return distDiff;
                });
        }
        // No client-side priority sort needed when no search active:
        // server already returns locations sorted by priority DESC, title ASC

        return filtered;
    }, [locations, selectedCompany, selectedArea, searchPosition, selectedRadius, calculateDistance, enableAreaFilter, usePrioritySort]);

    // Auto-select closest location when search position is set
    useEffect(() => {
        if (shouldAutoSelectClosestLocation && filteredLocations.length > 0) {
            setSelectedLocation(filteredLocations[0]);
            setShouldAutoSelectClosestLocation(false);
        }
    }, [shouldAutoSelectClosestLocation, filteredLocations]);

    // Get unique companies
    const companies = useMemo(() => Array.from(new Set(locations.map((l) => l.company).filter(Boolean))).sort(), [locations]);

    // Get unique areas
    const areas = useMemo(() => Array.from(new Set(locations.map((l) => l.area).filter(Boolean))).sort(), [locations]);

    // Memoize the onLocationSelect callback to prevent re-renders
    const handleMapLocationSelect = useCallback((location: Location) => {
        setSelectedLocation(location);
        setScrollToLocation(true);
        setForceZoomToLocation(true);
    }, []);

    // Scroll to selected location in list
    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];
        if (selectedLocation && scrollToLocation) {
            // For mobile, only scroll when in list view
            // For desktop, always scroll
            const shouldScroll = window.innerWidth >= 1024 || mobileView === 'list';

            if (shouldScroll) {
                const container = rootRef.current?.querySelector<HTMLElement>(
                    `[data-testid="locationfinder-list"][data-view="${window.innerWidth >= 1024 ? 'desktop' : 'mobile'}"]`
                );
                // Search within the active container to avoid hitting the hidden duplicate
                const element = container?.querySelector(`[data-location-id="${selectedLocation.uid}"]`) as HTMLElement | null;

                // Check if location exists in filtered list
                const locationInList = filteredLocations.find((loc) => loc.uid === selectedLocation.uid);

                if (element && container && locationInList) {
                    // Use scrollIntoView with better options

                    // Try browser-specific scrollIntoViewIfNeeded first (Chrome/Edge)
                    if ('scrollIntoViewIfNeeded' in element) {
                        (element as any).scrollIntoViewIfNeeded(true);
                    } else {
                        // Fallback to standard scrollIntoView
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }

                    // Fallback: Manual calculation if scrollIntoView doesn't work properly
                    timers.push(
                        setTimeout(() => {
                            const containerRect = container.getBoundingClientRect();
                            const elementRect = element.getBoundingClientRect();

                            // If element is not visible, force scroll
                            if (elementRect.width === 0 || elementRect.height === 0) {
                                // Try again after a longer delay
                                timers.push(
                                    setTimeout(() => {
                                        const newElementRect = element.getBoundingClientRect();
                                        if (newElementRect.width > 0 && newElementRect.height > 0) {
                                            element.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                                inline: 'nearest',
                                            });
                                        } else {
                                            // Calculate position by summing previous elements

                                            const wrapper = container.querySelector('.space-y-0');
                                            if (wrapper) {
                                                let targetPosition = 0;
                                                const children = wrapper.children;

                                                for (let i = 0; i < children.length; i++) {
                                                    // Identity check: element is one of these children, matched by id before
                                                    if (children[i] === element) {
                                                        break;
                                                    }
                                                    targetPosition += (children[i] as HTMLElement).offsetHeight;
                                                }

                                                const scrollPosition = targetPosition - 10; // 10px Abstand vom oberen Rand
                                                container.scrollTop = scrollPosition;
                                            }
                                        }
                                    }, 200)
                                );
                            } else if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
                                const elementOffset = (element as HTMLElement).offsetTop;
                                const containerOffset = (container as HTMLElement).offsetTop;
                                const scrollTop = elementOffset - containerOffset - container.clientHeight / 2 + (element as HTMLElement).offsetHeight / 2;
                                container.scrollTop = scrollTop;
                            }
                        }, 100)
                    );
                }
            }

            // Reset scroll flag
            Promise.resolve().then(() => setScrollToLocation(false));
        }

        return () => {
            timers.forEach(clearTimeout);
        };
    }, [selectedLocation, mobileView, filteredLocations, scrollToLocation]);

    // Force map to recenter when switching to map view with a selected location
    useEffect(() => {
        if (selectedLocation && mobileView === 'map' && window.innerWidth < 1024) {
            // Small delay to ensure map is visible
            const timer = setTimeout(() => {
                setMapCenter({
                    lat: selectedLocation.latitude,
                    lng: selectedLocation.longitude,
                });
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [mobileView, selectedLocation]);

    // Handle search with geocoding
    const handleSearch = useCallback(
        (term: string) => {
            if (!term || !googleLoaded || !hasConsent || !geocoderRef.current) {
                setSearchPosition(null);
                return;
            }

            // Geocode the search term using cached geocoder instance
            try {
                geocoderRef.current.geocode({ address: term }, (results, status) => {
                    if (status === 'OK' && results?.[0]?.geometry?.location) {
                        const lat = results[0].geometry.location.lat();
                        const lng = results[0].geometry.location.lng();
                        setMapCenter({ lat, lng });
                        setSearchPosition({ lat, lng });
                        setForceZoomToLocation(false); // Reset single-location zoom for new search
                        // Auto-select the first (closest) location when search position is set
                        setShouldAutoSelectClosestLocation(true);
                    } else {
                        setSearchPosition(null);
                    }
                });
            } catch {
                setSearchPosition(null);
            }
        },
        [googleLoaded, hasConsent]
    );

    // Handle place selection from autocomplete suggestions
    const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
        // Only update the input field with selected place
        // Do NOT auto-apply the search - let user press Enter or click Search button
        searchTermRef.current = place.formatted_address || place.name || '';
    }, []);

    // Handle GPS button location request
    const handleLocationRequest = useCallback((lat: number, lng: number) => {
        setMapCenter({ lat, lng });
        setSearchPosition({ lat, lng });
        setForceZoomToLocation(false); // Reset single-location zoom for new search
        // Auto-select the closest location after GPS position is set (matching text search behavior)
        setShouldAutoSelectClosestLocation(true);
    }, []);

    // Handle "Show on Map" button click — zoom to single location without clearing search results
    const handleShowOnMap = useCallback((location: Location) => {
        setSelectedLocation(location);
        setScrollToLocation(false);
        setForceZoomToLocation(true);
        setMapCenter({ lat: location.latitude, lng: location.longitude });
    }, []);

    // Same thing from the mobile list, which additionally flips over to the map. Both handlers are
    // stable so LocationCard's React.memo actually holds.
    const handleShowOnMapMobile = useCallback(
        (location: Location) => {
            handleShowOnMap(location);
            setMobileView('map');
        },
        [handleShowOnMap]
    );

    // Marker click on the mobile map, which flips back to the list. Stable for the same reason.
    const handleMapLocationSelectMobile = useCallback(
        (location: Location) => {
            handleMapLocationSelect(location);
            setMobileView('list');
        },
        [handleMapLocationSelect]
    );

    return (
        <div ref={rootRef} data-testid="locationfinder" data-instance-id={instanceScope} className={clsx('c-location-finder', className)}>
            {/* Header Section - Only render if any content exists */}
            {(t.topline || t.headline || t.description) && (
                <div className="mb-10">
                    {/* Topline - full width */}
                    {t.topline && (
                        <div className="mb-4">
                            <Typography variant="topline" as="div">
                                {t.topline}
                            </Typography>
                        </div>
                    )}

                    {/* Headline and Description - side by side */}
                    {(t.headline || t.description) && (
                        <div className="grid-gap grid grid-cols-6 lg:grid-cols-12">
                            {t.headline && (
                                <div className="col-span-6 lg:col-span-5">
                                    <Typography variant="headline-md" as="h2">
                                        {t.headline}
                                    </Typography>
                                </div>
                            )}

                            {t.description && (
                                <div className="col-span-6 lg:col-span-7">
                                    <Typography variant="copy" as="div">
                                        {t.description}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Content Section */}
            <div className="grid-gap grid grid-cols-6 lg:grid-cols-12">
                <div className="col-span-6 lg:col-span-12">
                    {/* Content Grid - Using TÜV NORD Grid System */}
                    <div className="grid-gap grid grid-cols-6 lg:h-[956px] lg:grid-cols-12">
                        {/* Left Column - Filters and List */}
                        <div className="col-span-6 flex h-auto flex-col lg:col-span-4 lg:h-full lg:overflow-hidden lg:rounded-lg lg:border lg:border-gray-600">
                            {/* Search and Filter Section */}
                            <div className="border-b border-gray-600 p-3">
                                {/* Search Input */}
                                <div className="mb-2" data-testid="locationfinder-search">
                                    <InputLocationWithPlaces
                                        id={`location-search-${instanceScope}`}
                                        label={t.searchLabel}
                                        onPlaceSelect={handlePlaceSelect}
                                        onValueChange={(v) => {
                                            searchTermRef.current = v;
                                        }}
                                        onSearch={handleSearch}
                                        resetKey={inputResetKey}
                                        placeholder={t.searchPlaceholder}
                                        size={InputSizeTypes.md}
                                        showIcon={false}
                                        showLocationButton={googleLoaded && hasConsent}
                                        disabled={!googleLoaded || !hasConsent}
                                        onLocationRequest={handleLocationRequest}
                                        locationButtonLabel={t.useMyLocation}
                                        locationMessages={{
                                            notSupported: t.locationNotSupported,
                                            error: t.locationError,
                                            denied: t.locationDenied,
                                            unavailable: t.locationUnavailable,
                                            timeout: t.locationTimeout,
                                        }}
                                    />
                                </div>

                                {/* All Filters - Initially Collapsed */}
                                {showFilters && (
                                    <div className="mb-2 space-y-2">
                                        {/* Umkreis Select */}
                                        <div>
                                            <label className="mb-1 block">
                                                <Typography variant="micro-copy-medium" className="uppercase text-darkblue-700">
                                                    {t.radiusLabel}
                                                </Typography>
                                            </label>
                                            <select
                                                value={pendingRadius}
                                                onChange={(e) => setPendingRadius(e.target.value)}
                                                className="w-full rounded-md border border-gray-600 bg-white px-4 py-3"
                                            >
                                                {radiusOptions.map((radius) => (
                                                    <option key={radius} value={radius}>
                                                        {radiusLabels[radius] ?? `${radius}km`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Company Select */}
                                        <div>
                                            <label className="mb-1 block">
                                                <Typography variant="micro-copy-medium" className="uppercase text-darkblue-700">
                                                    {t.companyLabel}
                                                </Typography>
                                            </label>
                                            <select
                                                value={pendingCompany}
                                                onChange={(e) => setPendingCompany(e.target.value)}
                                                className="w-full rounded-md border border-gray-600 bg-white px-4 py-3"
                                            >
                                                <option value="">{t.selectAll}</option>
                                                {companies.map((company) => (
                                                    <option key={company} value={company}>
                                                        {company}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Area Select - Only show if enabled */}
                                        {enableAreaFilter && (
                                            <div>
                                                <label className="mb-1 block">
                                                    <Typography variant="micro-copy-medium" className="uppercase text-darkblue-700">
                                                        {t.areaLabel}
                                                    </Typography>
                                                </label>
                                                <select
                                                    value={pendingArea}
                                                    onChange={(e) => setPendingArea(e.target.value)}
                                                    className="w-full rounded-md border border-gray-600 bg-white px-4 py-3"
                                                >
                                                    <option value="">{t.selectAll}</option>
                                                    {areas.map((area) => (
                                                        <option key={area} value={area}>
                                                            {area}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Filter Toggle inside filter section */}
                                        <div className="pt-2">
                                            <button
                                                onClick={() => setShowFilters(!showFilters)}
                                                className="flex w-full items-center justify-between py-2 text-darkblue-700 transition-colors hover:text-blue"
                                            >
                                                <Typography variant="copy" className="font-medium">
                                                    {t.filterHide}
                                                </Typography>
                                                <IconStatic name="ChevronUp" size="xs" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Filter Toggle when collapsed */}
                                {!showFilters && (
                                    <div className="mb-2">
                                        <button
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="flex w-full items-center justify-between py-2 text-darkblue-700 transition-colors hover:text-blue"
                                        >
                                            <Typography variant="copy" className="font-medium">
                                                {t.filterShow}
                                            </Typography>
                                            <IconStatic name="ChevronDown" size="xs" />
                                        </button>
                                    </div>
                                )}

                                {/* Buttons - Always visible */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => {
                                            // Reset to the plugin's configured starting point, not to "no filter at all":
                                            // an instance preselecting a company would otherwise widen from its own
                                            // locations to every location in the system.
                                            const initialArea = enableAreaFilter ? preselectedArea : '';
                                            setPendingRadius(defaultRadius);
                                            setPendingCompany(preselectedCompany);
                                            setPendingArea(initialArea);
                                            setSelectedRadius(defaultRadius);
                                            setSelectedCompany(preselectedCompany);
                                            setSelectedArea(initialArea);
                                            searchTermRef.current = '';
                                            setInputResetKey((k) => k + 1);
                                            setSearchPosition(null);
                                            setMapCenter({ lat: defaultCenterLat, lng: defaultCenterLng });
                                        }}
                                        className="flex w-full items-center justify-center gap-2 rounded-md border border-blue-100 bg-white px-4 py-2 text-blue transition-colors hover:border-blue-300 hover:bg-blue-10"
                                    >
                                        <Typography variant="copy-small" className="font-medium">
                                            {t.filterReset}
                                        </Typography>
                                        <IconStatic name="Refresh" size="xs" />
                                    </button>

                                    <div className="w-full">
                                        <Button
                                            variant="primary"
                                            size="md"
                                            className="!w-full"
                                            data-testid="locationfinder-search-submit"
                                            onClick={() => {
                                                // Apply pending filters
                                                setSelectedRadius(pendingRadius);
                                                setSelectedCompany(pendingCompany);
                                                setSelectedArea(pendingArea);

                                                // Apply search
                                                handleSearch(searchTermRef.current);
                                            }}
                                        >
                                            {t.searchButton}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Results Count */}
                            <div
                                data-testid="locationfinder-result-count"
                                data-count={filteredLocations.length}
                                className="border-b border-gray-600 px-4 py-2 text-center"
                            >
                                <Typography variant="copy-small" className="text-darkblue-700">
                                    {filteredLocations.length} {t.resultsFound}
                                </Typography>
                            </div>

                            {/* Mobile Tab Navigation */}
                            <div className="flex border-b border-gray-600 lg:hidden">
                                <button
                                    onClick={() => {
                                        setMobileView('list');
                                        setShowScrollTop(false);
                                    }}
                                    className={clsx(
                                        'flex flex-1 items-center justify-center gap-2 px-4 py-3 transition-colors',
                                        mobileView === 'list'
                                            ? 'border-b-2 border-blue bg-white text-darkblue'
                                            : 'hover:bg-gray-300 bg-gray-400 text-darkblue-700'
                                    )}
                                >
                                    <IconStatic name="List" size="xs" />
                                    <Typography variant="copy-small" className="font-medium">
                                        {t.listView}
                                    </Typography>
                                </button>
                                <button
                                    onClick={() => {
                                        setMobileView('map');
                                        setShowScrollTop(false);
                                    }}
                                    className={clsx(
                                        'flex flex-1 items-center justify-center gap-2 px-4 py-3 transition-colors',
                                        mobileView === 'map'
                                            ? 'border-b-2 border-blue bg-white text-darkblue'
                                            : 'hover:bg-gray-300 bg-gray-400 text-darkblue-700'
                                    )}
                                >
                                    <IconStatic name="Map" size="xs" />
                                    <Typography variant="copy-small" className="font-medium">
                                        {t.mapView}
                                    </Typography>
                                </button>
                            </div>

                            {/* Mobile View Content - List or Map. Mounted only below the breakpoint: the
                                CSS classes hide it on desktop, but React would still build a second card
                                for every location. */}
                            <div className="relative lg:hidden" style={{ height: mobileView === 'map' ? '480px' : 'auto' }}>
                                {isDesktop ? null : mobileView === 'list' ? (
                                    <div
                                        data-testid="locationfinder-list"
                                        data-view="mobile"
                                        className="overflow-y-auto bg-white"
                                        style={{ maxHeight: 'calc(100vh - 400px)' }}
                                        onScroll={(e) => {
                                            setShowScrollTop(e.currentTarget.scrollTop > 200);
                                        }}
                                    >
                                        {loading ? (
                                            <div className="py-12 text-center">
                                                <p className="text-gray-600">{t.loading}</p>
                                            </div>
                                        ) : filteredLocations.length === 0 ? (
                                            <div className="py-12 text-center">
                                                <p className="text-gray-600">{t.noResults}</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-0">
                                                {filteredLocations.map((location, index) => (
                                                    <LocationCard
                                                        key={location.uid}
                                                        location={location}
                                                        isLast={index === filteredLocations.length - 1}
                                                        isSelected={selectedLocation?.uid === location.uid}
                                                        onShowOnMap={handleShowOnMapMobile}
                                                        showOnMapText={t.showOnMap}
                                                        detailsText={t.details}
                                                        phoneLabel={t.phoneLabel}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative h-full" style={{ height: '480px' }}>
                                        {!hasConsent ? (
                                            <ConsentOverlay
                                                text={t.overlayText || ''}
                                                buttonLabel={t.overlayLabel || 'Cookies akzeptieren'}
                                                onAccept={handleConsentAccept}
                                            />
                                        ) : !isDesktop && googleLoaded ? (
                                            <GoogleMap
                                                locations={filteredLocations}
                                                center={mapCenter}
                                                selectedLocation={selectedLocation}
                                                searchPosition={searchPosition}
                                                forceZoomToLocation={forceZoomToLocation}
                                                useZoomlevel={useZoomlevel}
                                                zoomSingleMarker={zoomSingleMarker}
                                                zoomSelectedLocation={zoomSelectedLocation}
                                                zoomInitial={zoomInitial}
                                                onLocationSelect={handleMapLocationSelectMobile}
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center">
                                                <p className="text-gray-600">{googleLoadError ? t.googleMapsError : t.loading}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Desktop List - Always visible on desktop, mounted only above the breakpoint */}
                            <div className="relative hidden min-h-0 flex-1 lg:flex">
                                <div data-testid="locationfinder-list" data-view="desktop" className="absolute inset-0 overflow-y-auto bg-white">
                                    {!isDesktop ? null : loading ? (
                                        <div className="py-12 text-center">
                                            <p className="text-gray-600">{t.loading}</p>
                                        </div>
                                    ) : filteredLocations.length === 0 ? (
                                        <div className="py-12 text-center">
                                            <p className="text-gray-600">{t.noResults}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-0">
                                            {filteredLocations.map((location, index) => (
                                                <LocationCard
                                                    key={location.uid}
                                                    location={location}
                                                    isLast={index === filteredLocations.length - 1}
                                                    isSelected={selectedLocation?.uid === location.uid}
                                                    onShowOnMap={handleShowOnMap}
                                                    showOnMapText={t.showOnMap}
                                                    detailsText={t.details}
                                                    phoneLabel={t.phoneLabel}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Map (Desktop only) */}
                        <div
                            className="relative col-span-8 col-start-5 hidden overflow-hidden rounded-lg border border-gray-600 lg:block"
                            style={{ height: '100%' }}
                        >
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                                {!hasConsent ? (
                                    <ConsentOverlay
                                        text={t.overlayText || ''}
                                        buttonLabel={t.overlayLabel || 'Cookies akzeptieren'}
                                        onAccept={handleConsentAccept}
                                    />
                                ) : isDesktop && googleLoaded ? (
                                    <GoogleMap
                                        locations={filteredLocations}
                                        center={mapCenter}
                                        selectedLocation={selectedLocation}
                                        searchPosition={searchPosition}
                                        forceZoomToLocation={forceZoomToLocation}
                                        useZoomlevel={useZoomlevel}
                                        zoomSingleMarker={zoomSingleMarker}
                                        zoomSelectedLocation={zoomSelectedLocation}
                                        zoomInitial={zoomInitial}
                                        onLocationSelect={handleMapLocationSelect}
                                    />
                                ) : (
                                    <div className="flex h-full items-center justify-center">
                                        <p className="text-gray-600">{googleLoadError ? t.googleMapsError : t.loading}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll to Top Button - Mobile Only */}
            {showScrollTop && mobileView === 'list' && (
                <button
                    className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue text-white shadow-lg transition-all duration-300 hover:bg-darkblue lg:hidden"
                    onClick={() => {
                        // Find the mobile list container and scroll to top
                        const mobileList = rootRef.current?.querySelector<HTMLElement>('[data-testid="locationfinder-list"][data-view="mobile"]');
                        if (mobileList) {
                            mobileList.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                        setShowScrollTop(false);
                    }}
                    aria-label="Nach oben"
                >
                    <IconStatic name="ChevronUp" size="sm" />
                </button>
            )}
        </div>
    );
};

const LocationCard: React.FC<LocationCardProps> = React.memo(
    ({ location, isLast, isSelected, onShowOnMap, showOnMapText = 'Auf der Karte anzeigen', detailsText = 'Details anzeigen', phoneLabel = 'Tel.' }) => {
        // Format distance for display
        const formatDistance = (meters: number | undefined) => {
            if (!meters) return null;
            const km = meters / 1000;
            return km < 1 ? `${Math.round(meters)}m` : `${km.toFixed(1)}km`;
        };
        const fullAddress = `${location.address}, ${location.zip} ${location.place}`;

        return (
            <div
                data-testid="locationfinder-result"
                data-location-id={location.uid}
                className={clsx(
                    'cursor-pointer px-6 py-4 transition-colors',
                    isSelected ? 'border-l-4 border-l-blue bg-blue-10 pl-5' : 'hover:bg-gray-400',
                    !isLast && 'border-b border-gray-600'
                )}
                onClick={() => onShowOnMap?.(location)}
            >
                <div className="mb-2 flex items-start justify-between">
                    <div className="flex-1">
                        <Typography variant="copy-medium" as="h3" className="mb-1 text-darkblue">
                            {location.title}
                        </Typography>
                        <Typography variant="copy-small" className="text-darkblue-600">
                            {location.company}
                        </Typography>
                    </div>
                    {location.distance !== undefined && (
                        <Typography variant="copy-small" className="ml-4 flex-shrink-0 text-darkblue-700">
                            {formatDistance(location.distance)}
                        </Typography>
                    )}
                </div>

                <div className="mb-3">
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography variant="copy-small" className="text-darkblue-700">
                            {location.address}
                        </Typography>
                        <Typography variant="copy-small" className="text-darkblue-700">
                            {location.zip} {location.place}
                        </Typography>
                    </a>
                </div>

                {/* Phone and Email */}
                {(location.phoneNumber || location.emailAddress) && (
                    <div className="mb-3 space-y-1">
                        {location.phoneNumber && (
                            <div className="flex items-start">
                                <Typography variant="copy-small" className="text-darkblue-700">
                                    {phoneLabel}:&nbsp;
                                </Typography>
                                <a href={`tel:${location.phoneNumber}`} className="text-sm text-blue hover:underline" onClick={(e) => e.stopPropagation()}>
                                    {location.phoneNumber}
                                </a>
                                {location.emailAddress && (
                                    <>
                                        <span className="mx-2 text-darkblue-700">/</span>
                                        <a
                                            href={`mailto:${location.emailAddress}`}
                                            className="break-all text-sm text-blue hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {location.emailAddress}
                                        </a>
                                    </>
                                )}
                            </div>
                        )}
                        {!location.phoneNumber && location.emailAddress && (
                            <a
                                href={`mailto:${location.emailAddress}`}
                                className="break-all text-sm text-blue hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {location.emailAddress}
                            </a>
                        )}
                    </div>
                )}

                {location.detailUrl && (
                    <a
                        href={location.detailUrl}
                        data-testid="locationfinder-detail-link"
                        // Every card repeats the same link text, so a screen reader's link list would
                        // otherwise read "Details anzeigen" N times without saying which location.
                        aria-label={`${detailsText}: ${location.title}`}
                        className="mb-2 block w-full rounded-md bg-blue px-4 py-2 text-center text-white transition-colors hover:bg-darkblue"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Typography variant="copy-small" className="font-normal">
                            {detailsText}
                        </Typography>
                    </a>
                )}

                <button
                    data-testid="locationfinder-show-on-map"
                    aria-label={`${showOnMapText}: ${location.title}`}
                    className="w-full rounded-md border border-gray-600 bg-white px-4 py-2 text-darkblue transition-colors hover:border-darkblue-700 hover:bg-gray-400"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShowOnMap?.(location);
                    }}
                >
                    <Typography variant="copy-small" className="font-normal">
                        {showOnMapText}
                    </Typography>
                </button>
            </div>
        );
    }
);

export default LocationFinder;
